/**
 * Cart Upsell Navigation
 * Adds arrow navigation and dot pagination functionality for cart upsell products
 * Works with both Flickity slider mode and native scroll mode
 */

(function() {
  'use strict';

  class CartUpsellNavigation {
    constructor() {
      this.pollingTimer = null;
      this.init();
    }

    init() {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.setupAll());
      } else {
        this.setupAll();
      }

      // Tous les événements d'ouverture/mise à jour du cart drawer
      const events = [
        'cart:updated',
        'cart:refresh',
        'drawer:opened',
        'theme:cart-drawer:open',
        'theme:cart:open',
        'theme:cart:refresh',
      ];

      events.forEach(eventName => {
        document.addEventListener(eventName, () => {
          // Polling léger uniquement après ouverture du drawer
          // S'arrête dès que les produits sont trouvés
          this.startPolling();
        });
      });
    }

    startPolling() {
      // Éviter les pollings en parallèle
      if (this.pollingTimer) return;

      let attempts = 0;
      const maxAttempts = 20; // max 2 secondes (20 × 100ms)

      const poll = () => {
        attempts++;
        const ready = this.setupAll();

        if (ready || attempts >= maxAttempts) {
          this.pollingTimer = null;
          return;
        }

        this.pollingTimer = setTimeout(poll, 100);
      };

      this.pollingTimer = setTimeout(poll, 100);
    }

    setupAll() {
      const widgets = document.querySelectorAll('.cart__widget--products');
      let anyReady = false;

      widgets.forEach(widget => {
        const cartUpsellSlider = widget.querySelector('cart-upsell-slider');
        if (!cartUpsellSlider) return;

        const hasProducts = cartUpsellSlider.querySelector('quick-add-product');
        if (!hasProducts) return;

        if (!widget._upsellNavReady) {
          this.setupNavigation(cartUpsellSlider, widget);
        }

        anyReady = true;
      });

      return anyReady;
    }

    setupNavigation(cartUpsellSliderEl, widget) {
      const prevBtn = widget.querySelector('.cart__widget__arrow--prev');
      const nextBtn = widget.querySelector('.cart__widget__arrow--next');
      const dotsContainer = widget.querySelector('.cart__widget__dots');

      if (!prevBtn || !nextBtn || !dotsContainer) return;

      widget._upsellNavReady = true;

      const sliderComponent = cartUpsellSliderEl.querySelector('slider-component');

      if (sliderComponent) {
        this.setupFlickityNavigation(sliderComponent, prevBtn, nextBtn, dotsContainer);
      } else {
        this.setupScrollNavigation(cartUpsellSliderEl, prevBtn, nextBtn, dotsContainer);
      }
    }

    setupFlickityNavigation(sliderComponent, prevBtn, nextBtn, dotsContainer) {
      const checkFlickity = () => {
        const flickityEl = sliderComponent.querySelector('.flickity-enabled');

        if (flickityEl && flickityEl.flickityInstance) {
          const flkty = flickityEl.flickityInstance;

          this.createFlickityDots(flkty, dotsContainer);

          const updateNav = () => {
            prevBtn.disabled = flkty.selectedIndex === 0;
            nextBtn.disabled = flkty.selectedIndex === flkty.slides.length - 1;
            const dots = dotsContainer.querySelectorAll('.cart__widget__dot');
            dots.forEach((dot, index) => {
              dot.classList.toggle('is-active', index === flkty.selectedIndex);
            });
          };

          prevBtn.addEventListener('click', () => flkty.previous());
          nextBtn.addEventListener('click', () => flkty.next());
          flkty.on('change', updateNav);
          flkty.on('select', updateNav);
          updateNav();

        } else {
          setTimeout(checkFlickity, 100);
        }
      };

      checkFlickity();
    }

    createFlickityDots(flkty, dotsContainer) {
      dotsContainer.innerHTML = '';
      flkty.slides.forEach((_, index) => {
        const dot = document.createElement('button');
        dot.classList.add('cart__widget__dot');
        dot.setAttribute('type', 'button');
        dot.setAttribute('aria-label', `Go to slide ${index + 1}`);
        if (index === 0) dot.classList.add('is-active');
        dot.addEventListener('click', () => flkty.select(index));
        dotsContainer.appendChild(dot);
      });
    }

    setupScrollNavigation(cartUpsellSliderEl, prevBtn, nextBtn, dotsContainer) {
      const productsContainer = cartUpsellSliderEl.querySelector('.cart__widget__products');
      if (!productsContainer) return;

      const items = Array.from(productsContainer.querySelectorAll('quick-add-product'));
      if (items.length === 0) return;

      let currentIndex = 0;

      const updateNavigation = () => {
        const scrollLeft = productsContainer.scrollLeft;
        const scrollMax = productsContainer.scrollWidth - productsContainer.offsetWidth;

        prevBtn.disabled = scrollLeft <= 1;
        nextBtn.disabled = scrollLeft >= scrollMax - 1;

        let closestIndex = 0;
        let minDistance = Infinity;
        items.forEach((item, index) => {
          const itemLeft = item.offsetLeft - productsContainer.offsetLeft;
          const distance = Math.abs(scrollLeft - itemLeft);
          if (distance < minDistance) {
            minDistance = distance;
            closestIndex = index;
          }
        });

        currentIndex = closestIndex;

        const dots = dotsContainer.querySelectorAll('.cart__widget__dot');
        dots.forEach((dot, index) => {
          dot.classList.toggle('is-active', index === currentIndex);
        });
      };

      const goToCard = (cardIndex) => {
        if (cardIndex < 0 || cardIndex >= items.length) return;
        currentIndex = cardIndex;
        const item = items[cardIndex];
        productsContainer.scrollTo({
          left: item.offsetLeft - productsContainer.offsetLeft,
          behavior: 'smooth'
        });
        updateNavigation();
      };

      const createDots = () => {
        dotsContainer.innerHTML = '';
        items.forEach((_, i) => {
          const dot = document.createElement('button');
          dot.classList.add('cart__widget__dot');
          dot.setAttribute('type', 'button');
          dot.setAttribute('aria-label', `Go to product ${i + 1}`);
          if (i === 0) dot.classList.add('is-active');
          dot.addEventListener('click', () => goToCard(i));
          dotsContainer.appendChild(dot);
        });
        updateNavigation();
      };

      // Scroll natif
      let scrollTimeout;
      productsContainer.addEventListener('scroll', () => {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(updateNavigation, 100);
      });

      // Touch events pour swipe mobile
      let touchStartX = 0;
      productsContainer.addEventListener('touchstart', (e) => {
        touchStartX = e.touches[0].clientX;
      }, { passive: true });

      productsContainer.addEventListener('touchend', (e) => {
        const diff = touchStartX - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 30) {
          goToCard(diff > 0 ? currentIndex + 1 : currentIndex - 1);
        }
      }, { passive: true });

      // Flèches
      prevBtn.addEventListener('click', () => goToCard(currentIndex - 1));
      nextBtn.addEventListener('click', () => goToCard(currentIndex + 1));

      // Resize
      let resizeTimeout;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(createDots, 250);
      });

      setTimeout(createDots, 200);
    }
  }

  if (!customElements.get('cart-upsell-slider')) {
    customElements.define('cart-upsell-slider', class extends HTMLElement {
      constructor() { super(); }
    });
  }

  new CartUpsellNavigation();

})();